/**
 * region-ops.ts - Item 23 + Item 24 + Item 26 + Item 27 (v0.2.4.2)
 *
 * Pure functions for region merge, delete, fill-small-islands, and re-flood
 * operations. Each function takes the current DemoState fields it needs and
 * returns a StatePatch with the changed fields only. No mutation of input
 * data; no DOM; no side effects.
 *
 * Principle A4 (plan): every region operation is a pure function
 *   (state fields) → StatePatch
 *
 * Portability: this file is under apps/demo/src/util/ and is covered by the
 * check:portable directory-scope rule. No document/window/fetch/Node globals
 * are used.
 */

import { generateUuid } from './uuid';
import type { RegionConfig, RegionEntry } from '../state';

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

/**
 * The subset of DemoState fields that region operations read and write.
 * Using a narrow interface keeps the functions testable without constructing
 * a full DemoState.
 *
 * `labelMap` and `regions` are required so the returned patch also updates
 * the fields that the render pipeline reads. The render loop uses
 * `state.labelMap` + `buildRegions(state)` (which reads `state.regions`).
 * Without updating them, region-op changes are reflected in the panel UI
 * but not in the dither output.
 *
 * `width` and `height` are required for rebuilding the labelMap ImageData
 * (pixel count = width × height).
 */
export interface RegionOpInput {
  regionEntries: RegionEntry[];
  regionLabelPlane: Uint8Array;
  activeRegionUuids: string[];
  /** Width of the source image (pixels). Required for labelMap rebuild. */
  width: number;
  /** Height of the source image (pixels). Required for labelMap rebuild. */
  height: number;
  /** Current labelMap ImageData. Returned unchanged on no-op paths. */
  labelMap: ImageData | null;
  /** Current RegionConfig array. Returned unchanged on no-op paths. */
  regions: RegionConfig[];
}

/**
 * Patch returned by a region operation. Contains only the fields that changed.
 * Callers apply it via store.patch(patch).
 *
 * `labelMap` and `regions` are always included so the render pipeline
 * receives up-to-date values - avoiding the stale-render bug where the
 * panel updates but the dithered output does not.
 */
export interface RegionOpPatch {
  regionEntries: RegionEntry[];
  regionLabelPlane: Uint8Array;
  activeRegionUuids: string[];
  labelMap: ImageData | null;
  regions: RegionConfig[];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Rebuild a labelMap ImageData from a regionLabelPlane Uint8Array.
 *
 * Convention: R channel = label index, G = 0, B = 0, A = 255.
 * This matches the output of `parseLabelmapImage` so the render pipeline
 * can consume it without any conversion.
 *
 * Returns null when width or height is 0 (no image loaded).
 */
function rebuildLabelMap(
  labelPlane: Uint8Array,
  width: number,
  height: number,
): ImageData | null {
  if (width === 0 || height === 0) return null;
  const data = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < labelPlane.length; i++) {
    data[i * 4] = labelPlane[i]!;     // R = label index
    data[i * 4 + 1] = 0;              // G
    data[i * 4 + 2] = 0;              // B
    data[i * 4 + 3] = 255;            // A = fully opaque
  }
  return new ImageData(data, width, height);
}

/**
 * Derive a RegionConfig[] from a RegionEntry[].
 *
 * Mirrors the shape produced by `resolveRegionMethods` for the legacy
 * pipeline compatibility layer: each entry becomes a RegionConfig with
 * `override: null`. Per-region method overrides are resolved later by
 * `buildRegions(state)` / `resolveRegionMethods(state)` in the render
 * loop, so setting override to null here is correct - the resolved method
 * is never stored in `state.regions`.
 */
function regionConfigsFromEntries(entries: RegionEntry[]): RegionConfig[] {
  return entries.map((e) => ({
    label: e.label,
    rgb: e.rgb,
    override: null,
  }));
}

// ---------------------------------------------------------------------------
// Item 23 - mergeRegions
// ---------------------------------------------------------------------------

/**
 * Merge one or more source regions into a target region.
 *
 * Algorithm:
 *   1. Identify the target region's label and each source region's label.
 *   2. Rewrite regionLabelPlane: every pixel whose label is any of the source
 *      labels becomes the target label. Target pixels are unchanged. All other
 *      pixels are unchanged.
 *   3. Prune regionEntries: retain the target entry; drop all source entries.
 *   4. Set activeRegionUuids to [targetUuid] so the merged region is selected.
 *
 * If targetUuid or any sourceUuid is not found in regionEntries, those missing
 * UUIDs are silently ignored (idempotent guard). If sourceUuids is empty, the
 * function returns unchanged data (no-op merge).
 *
 * @param state    - Narrow slice of DemoState containing the relevant fields.
 * @param targetUuid  - UUID of the region that will absorb the source regions.
 * @param sourceUuids - UUIDs of regions to be absorbed into the target.
 * @returns A StatePatch with the updated fields.
 */
export function mergeRegions(
  state: RegionOpInput,
  targetUuid: string,
  sourceUuids: string[],
): RegionOpPatch {
  const { regionEntries, regionLabelPlane, width, height } = state;

  // Resolve entries.
  const targetEntry = regionEntries.find((e) => e.uuid === targetUuid);
  if (!targetEntry) {
    // Target not found - return unchanged state.
    return {
      regionEntries,
      regionLabelPlane,
      activeRegionUuids: state.activeRegionUuids,
      labelMap: state.labelMap,
      regions: state.regions,
    };
  }

  // Collect the labels of the source regions to be merged in.
  const sourceLabelSet = new Set<number>();
  const sourceUuidSet = new Set<string>(sourceUuids);
  for (const entry of regionEntries) {
    if (sourceUuidSet.has(entry.uuid) && entry.uuid !== targetUuid) {
      sourceLabelSet.add(entry.label);
    }
  }

  if (sourceLabelSet.size === 0) {
    // Nothing to merge (sources empty or all equal to target) - no-op.
    return {
      regionEntries,
      regionLabelPlane,
      activeRegionUuids: [targetUuid],
      labelMap: state.labelMap,
      regions: state.regions,
    };
  }

  // Rewrite label plane: source labels → target label.
  const targetLabel = targetEntry.label;
  const newLabelPlane = new Uint8Array(regionLabelPlane);
  for (let i = 0; i < newLabelPlane.length; i++) {
    if (sourceLabelSet.has(newLabelPlane[i]!)) {
      newLabelPlane[i] = targetLabel;
    }
  }

  // Prune entries: keep target, drop sources.
  const newEntries = regionEntries.filter(
    (e) => !sourceUuidSet.has(e.uuid) || e.uuid === targetUuid,
  );

  // Rebuild render-pipeline fields so the dither output stays in sync.
  const newLabelMap = rebuildLabelMap(newLabelPlane, width, height);
  const newRegions = regionConfigsFromEntries(newEntries);

  return {
    regionEntries: newEntries,
    regionLabelPlane: newLabelPlane,
    activeRegionUuids: [targetUuid],
    labelMap: newLabelMap,
    regions: newRegions,
  };
}

// ---------------------------------------------------------------------------
// Item 24 - deleteRegions
// ---------------------------------------------------------------------------

/**
 * Delete one or more regions, setting their pixels to label 0 (unlabelled).
 *
 * Algorithm:
 *   1. Collect the labels of the regions to delete.
 *   2. Rewrite regionLabelPlane: every pixel whose label is any of the deleted
 *      labels becomes 0. All other pixels are unchanged.
 *   3. Prune regionEntries: drop the deleted entries.
 *   4. Update activeRegionUuids:
 *      - If none of the deleted UUIDs were in activeRegionUuids, leave
 *        activeRegionUuids unchanged.
 *      - Otherwise replace with [firstRemainingUuid], where firstRemainingUuid
 *        is the first entry UUID in the surviving entries array. If no entries
 *        survive, set activeRegionUuids to [].
 *
 * If a uuid in `uuids` is not found in regionEntries, it is silently ignored.
 * If `uuids` is empty, the function returns unchanged data (no-op).
 *
 * Pairs with pipeline.ts applyAlphaForLabelZero: pixels at label 0 are
 * rendered as alpha=0 (transparent) by the multi-region dither path.
 *
 * @param state - Narrow slice of DemoState containing the relevant fields.
 * @param uuids - UUIDs of regions to delete.
 * @returns A StatePatch with the updated fields.
 */
export function deleteRegions(
  state: RegionOpInput,
  uuids: string[],
): RegionOpPatch {
  const { regionEntries, regionLabelPlane, activeRegionUuids, width, height } = state;

  if (uuids.length === 0) {
    return {
      regionEntries,
      regionLabelPlane,
      activeRegionUuids,
      labelMap: state.labelMap,
      regions: state.regions,
    };
  }

  const deleteUuidSet = new Set<string>(uuids);

  // Collect labels of entries being deleted.
  const deleteLabelSet = new Set<number>();
  for (const entry of regionEntries) {
    if (deleteUuidSet.has(entry.uuid)) {
      deleteLabelSet.add(entry.label);
    }
  }

  // Rewrite label plane: deleted labels → 0.
  const newLabelPlane = new Uint8Array(regionLabelPlane);
  for (let i = 0; i < newLabelPlane.length; i++) {
    if (deleteLabelSet.has(newLabelPlane[i]!)) {
      newLabelPlane[i] = 0;
    }
  }

  // Prune entries: drop deleted ones.
  const newEntries = regionEntries.filter((e) => !deleteUuidSet.has(e.uuid));

  // Update selection: only change if a deleted uuid was active.
  const deletedAnyActive = activeRegionUuids.some((u) => deleteUuidSet.has(u));
  let newActive: string[];
  if (!deletedAnyActive) {
    newActive = activeRegionUuids;
  } else {
    // Replace selection with the first surviving entry, or empty if all deleted.
    const firstSurviving = newEntries[0]?.uuid;
    newActive = firstSurviving !== undefined ? [firstSurviving] : [];
  }

  // Rebuild render-pipeline fields so the dither output stays in sync.
  const newLabelMap = rebuildLabelMap(newLabelPlane, width, height);
  const newRegions = regionConfigsFromEntries(newEntries);

  return {
    regionEntries: newEntries,
    regionLabelPlane: newLabelPlane,
    activeRegionUuids: newActive,
    labelMap: newLabelMap,
    regions: newRegions,
  };
}

// ---------------------------------------------------------------------------
// Item 26 - fillSmallIslands
// ---------------------------------------------------------------------------

/**
 * Extended input for fillSmallIslands - requires image dimensions to perform
 * the 4-connected flood-fill.
 *
 * Note: `width` and `height` are already present on `RegionOpInput` as of
 * the Critical-1 fix. This alias is retained for backwards-compatible test
 * imports.
 */
export type FillIslandsInput = RegionOpInput;

/**
 * Fill small isolated pixel regions (islands) by assigning each island's
 * pixels to the label of its most-adjacent 4-connected neighbour.
 *
 * Algorithm:
 *   1. Flood-fill the label plane to identify all 4-connected components and
 *      their labels, pixel counts, and border-contact counts per neighbour label.
 *   2. For each component whose pixelCount < minPixels (an island), select
 *      the winning neighbour label: highest border-contact count, lowest label
 *      as tiebreak. Border-contact count is locality-aware: an island touching
 *      3 px of label A and 1 px of label B merges into A regardless of total
 *      pixel counts of A and B globally.
 *   3. Process islands in ascending size order so that the smallest are
 *      absorbed first.
 *   4. Rewrite label plane: island pixels → winning neighbour label.
 *   5. Drop any RegionEntry that has zero pixels remaining in the rewritten
 *      plane. Update activeRegionUuids if any dropped entry was active.
 *
 * Edge cases:
 *   - Island with no eligible neighbours (entire image is one label): no-op
 *     for that component.
 *   - Label-0 (unlabelled / background) components are eligible for merging
 *     like any other label - there is no special treatment.
 *   - If minPixels <= 0, no components qualify and the function returns
 *     unchanged data.
 *
 * @param state      - Narrow slice of DemoState including image dimensions.
 * @param minPixels  - Components with fewer pixels than this are filled.
 * @returns A StatePatch with the updated fields.
 */
export function fillSmallIslands(
  state: FillIslandsInput,
  minPixels: number,
): RegionOpPatch {
  const { regionEntries, regionLabelPlane, activeRegionUuids, width, height } = state;

  // Nothing to do if minPixels is not positive or the plane is empty.
  if (minPixels <= 0 || regionLabelPlane.length === 0 || width === 0 || height === 0) {
    return {
      regionEntries,
      regionLabelPlane,
      activeRegionUuids,
      labelMap: state.labelMap,
      regions: state.regions,
    };
  }

  // -------------------------------------------------------------------------
  // Phase 1: Flood-fill to identify connected components.
  //
  // componentId[i] maps pixel index i to its component id (0-based).
  // -1 = unvisited.
  // -------------------------------------------------------------------------

  const n = width * height;
  const componentId = new Int32Array(n).fill(-1);

  // Per-component data.
  type ComponentInfo = {
    label: number;
    pixels: number[];
    // Adjacency: Map from neighbour label → contact count (border pixels).
    // Used only to detect which labels are adjacent.
    neighbourCounts: Map<number, number>;
  };

  const components: ComponentInfo[] = [];

  // BFS queue using a pre-allocated flat array for performance.
  const queue = new Int32Array(n);

  for (let start = 0; start < n; start++) {
    if (componentId[start] !== -1) continue;

    const compIdx = components.length;
    const label = regionLabelPlane[start]!;
    const info: ComponentInfo = { label, pixels: [], neighbourCounts: new Map() };
    components.push(info);

    // BFS from `start`.
    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    componentId[start] = compIdx;

    while (head < tail) {
      const idx = queue[head++]!;
      info.pixels.push(idx);

      const x = idx % width;
      const y = (idx - x) / width;

      // 4-connected neighbours: up, down, left, right.
      const neighbours: number[] = [];
      if (y > 0) neighbours.push(idx - width);          // up
      if (y < height - 1) neighbours.push(idx + width); // down
      if (x > 0) neighbours.push(idx - 1);              // left
      if (x < width - 1) neighbours.push(idx + 1);      // right

      for (const nb of neighbours) {
        const nbCompId = componentId[nb];
        if (nbCompId === -1) {
          // Unvisited, same component if same label; otherwise separate.
          if (regionLabelPlane[nb] === label) {
            componentId[nb] = compIdx;
            queue[tail++] = nb;
          }
          // Note: if different label, we haven't assigned it yet (nbCompId -1
          // means unvisited from its own BFS). We record cross-label adjacency
          // below via the label check.
        }
        // Record neighbour label for adjacency (cross-component edges only).
        // We check the label to avoid counting same-label-different-component
        // adjacency edges here - those will be handled by the later merge step.
        const nbLabel = regionLabelPlane[nb]!;
        if (nbLabel !== label) {
          const prev = info.neighbourCounts.get(nbLabel) ?? 0;
          info.neighbourCounts.set(nbLabel, prev + 1);
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // Phase 2: Identify small components and sort by ascending size.
  // -------------------------------------------------------------------------

  const smallComps = components
    .map((comp, id) => ({ id, comp }))
    .filter(({ comp }) => comp.pixels.length < minPixels)
    .sort((a, b) => a.comp.pixels.length - b.comp.pixels.length);

  if (smallComps.length === 0) {
    return {
      regionEntries,
      regionLabelPlane,
      activeRegionUuids,
      labelMap: state.labelMap,
      regions: state.regions,
    };
  }

  // -------------------------------------------------------------------------
  // Phase 3: Rewrite label plane - process islands smallest-first.
  //
  // Winner selection: pick the neighbour with the highest border-contact count
  // (number of shared boundary pixels). This is locality-aware: an island
  // touching 3 px of label A and 1 px of label B merges into A even when B is
  // globally larger. Tie-break: lowest label number (stable, deterministic).
  //
  // neighbourCounts are frozen from Phase 1's BFS, but we filter winners by
  // live labelPixelCount so a label that was already emptied by an earlier
  // merge in this pass is never picked.
  // -------------------------------------------------------------------------

  const newLabelPlane = new Uint8Array(regionLabelPlane);

  // Track which labels still have pixels remaining after merges so we can
  // (a) filter out emptied labels during winner selection, and
  // (b) identify emptied entries in Phase 4 without re-scanning the full plane.
  const labelPixelCount = new Map<number, number>();
  for (const comp of components) {
    const prev = labelPixelCount.get(comp.label) ?? 0;
    labelPixelCount.set(comp.label, prev + comp.pixels.length);
  }

  for (const { comp } of smallComps) {
    // Skip if no pixels (should not happen but guard against edge corruption).
    if (comp.pixels.length === 0) continue;

    // Pick winner: highest border-contact count for a neighbouring label;
    // lowest label number as tiebreak.
    // Skip any neighbour label whose live pixel count has dropped to zero -
    // it was already absorbed into another label earlier in this pass.
    let winnerLabel: number | null = null;
    let winnerCount = -1;

    for (const [nbLabel, contactCount] of comp.neighbourCounts) {
      const liveCount = labelPixelCount.get(nbLabel) ?? 0;
      if (liveCount < minPixels) continue; // skip labels that are themselves still small islands (we don't merge small-into-small)
      if (
        contactCount > winnerCount ||
        (contactCount === winnerCount && winnerLabel !== null && nbLabel < winnerLabel)
      ) {
        winnerLabel = nbLabel;
        winnerCount = contactCount;
      }
    }

    if (winnerLabel === null) {
      // No live neighbour - all neighbours were already absorbed, or the island
      // has no cross-label adjacency at all. Leave the island in place.
      continue;
    }

    // Rewrite pixels.
    const islandPixelCount = comp.pixels.length;
    for (const pixelIdx of comp.pixels) {
      newLabelPlane[pixelIdx] = winnerLabel;
    }

    // Update pixel counts so emptied-label detection in Phase 4 is accurate.
    const prevIslandCount = labelPixelCount.get(comp.label) ?? 0;
    labelPixelCount.set(comp.label, prevIslandCount - islandPixelCount);

    const prevWinnerCount = labelPixelCount.get(winnerLabel) ?? 0;
    labelPixelCount.set(winnerLabel, prevWinnerCount + islandPixelCount);
  }

  // -------------------------------------------------------------------------
  // Phase 4: Prune emptied RegionEntries; update activeRegionUuids.
  // -------------------------------------------------------------------------

  // A label is empty when its tracked pixel count has dropped to zero.
  const emptyLabels = new Set<number>();
  for (const [label, count] of labelPixelCount) {
    if (count <= 0) emptyLabels.add(label);
  }

  // Label 0 is unlabelled / background - it has no RegionEntry, so we only
  // need to prune non-zero labels that are now empty.
  const droppedUuids = new Set<string>();
  let newEntries = regionEntries;
  if (emptyLabels.size > 0) {
    newEntries = regionEntries.filter((e) => {
      if (emptyLabels.has(e.label)) {
        droppedUuids.add(e.uuid);
        return false;
      }
      return true;
    });
  }

  // Update selection if any dropped entry was active.
  let newActive = activeRegionUuids;
  if (droppedUuids.size > 0) {
    const hadActiveDrop = activeRegionUuids.some((u) => droppedUuids.has(u));
    if (hadActiveDrop) {
      const firstSurviving = newEntries[0]?.uuid;
      newActive = firstSurviving !== undefined ? [firstSurviving] : [];
    }
  }

  // Rebuild render-pipeline fields so the dither output stays in sync.
  const newLabelMap = rebuildLabelMap(newLabelPlane, width, height);
  const newRegions = regionConfigsFromEntries(newEntries);

  return {
    regionEntries: newEntries,
    regionLabelPlane: newLabelPlane,
    activeRegionUuids: newActive,
    labelMap: newLabelMap,
    regions: newRegions,
  };
}

// ---------------------------------------------------------------------------
// Item 27 - reflood
// ---------------------------------------------------------------------------

/**
 * Extended input for reflood - requires image dimensions to perform the
 * 4-connected flood-fill and to validate the seed coordinate.
 *
 * Note: `width` and `height` are now on `RegionOpInput`. This alias is
 * retained for call-site clarity.
 */
export type RefloodInput = RegionOpInput;

/**
 * Extended patch returned by reflood. Carries an optional status field
 * so the caller can detect the cap-exceeded or no-op edge cases without
 * inspecting the returned data.
 *
 * - 'ok'         - the component was split off into a new region.
 * - 'noop'       - the component covers the entire source region; nothing changed.
 * - 'cap'        - the new label would exceed the 255 cap; nothing changed.
 * - 'oob'        - seed is out of bounds or regionLabelPlane is empty.
 * - 'unlabelled' - seed lands on label 0 (background); nothing changed.
 */
export type RefloodStatus = 'ok' | 'noop' | 'cap' | 'oob' | 'unlabelled';

export interface RefloodPatch extends RegionOpPatch {
  status: RefloodStatus;
}

/**
 * Re-flood a region from a seed pixel, splitting a 4-connected component
 * out of an existing region into a new one.
 *
 * Algorithm:
 *   1. Validate seed: if (x, y) is out of bounds or the label plane is empty,
 *      return no-op with status 'oob'.
 *   2. Find the label at (x, y) in regionLabelPlane. If label is 0 (background),
 *      return no-op with status 'unlabelled'.
 *   3. Locate the RegionEntry for that label. If not found, return no-op.
 *   4. BFS from (x, y): identify the 4-connected component that starts there.
 *   5. Count all pixels of the source label to determine the total region size.
 *   6. If the component size equals the total region size (entire region is
 *      one connected component): return no-op with status 'noop'.
 *   7. Compute new label = max(existing labels) + 1. If > 255, return no-op
 *      with status 'cap' (and emit console.warn for diagnostics).
 *   8. Allocate new RegionEntry: fresh UUID, new label, same RGB as source,
 *      default userName via nameForLabel, null override, null stackId.
 *   9. Rewrite label plane: component pixels → new label.
 *  10. Return RefloodPatch + status 'ok'.
 *
 * @param state        - Narrow slice of DemoState including image dimensions.
 * @param seed         - Canvas-space pixel coordinate {x, y} (integer, 0-based).
 * @param nameForLabel - Pure function that returns a default userName string
 *                       for a given new label number. Injected to avoid a
 *                       direct i18n dependency in this util file.
 * @returns A RefloodPatch with the updated fields and a status discriminant.
 */
export function reflood(
  state: RefloodInput,
  seed: { x: number; y: number },
  nameForLabel: (label: number) => string,
): RefloodPatch {
  const { regionEntries, regionLabelPlane, activeRegionUuids, width, height } = state;

  const noOp = (status: RefloodStatus): RefloodPatch => ({
    regionEntries,
    regionLabelPlane,
    activeRegionUuids,
    labelMap: state.labelMap,
    regions: state.regions,
    status,
  });

  // Validate seed bounds and plane presence.
  if (regionLabelPlane.length === 0 || width === 0 || height === 0) {
    return noOp('oob');
  }

  // Minor 5 fix: use Math.floor (not Math.round) to match the pixel grid
  // convention; clamp to [0, dim-1] to stay within canvas bounds.
  const sx = Math.max(0, Math.min(width - 1, Math.floor(seed.x)));
  const sy = Math.max(0, Math.min(height - 1, Math.floor(seed.y)));

  // Additional guard: if the original (unclipped) coordinates were completely
  // out of range (negative before clamping), treat as oob.
  if (seed.x < 0 || seed.y < 0 || seed.x >= width || seed.y >= height) {
    return noOp('oob');
  }

  const seedIdx = sy * width + sx;
  const sourceLabel = regionLabelPlane[seedIdx]!;

  // Label 0 is background - not a named region.
  if (sourceLabel === 0) {
    return noOp('unlabelled');
  }

  // Find the source RegionEntry.
  const sourceEntry = regionEntries.find((e) => e.label === sourceLabel);
  if (!sourceEntry) {
    // No entry for this label - data inconsistency, treat as no-op.
    return noOp('noop');
  }

  // -------------------------------------------------------------------------
  // Phase 1: BFS from seed to find the 4-connected component.
  // -------------------------------------------------------------------------

  const n = width * height;
  const visited = new Uint8Array(n);
  const queue = new Int32Array(n);
  const component: number[] = [];

  let head = 0;
  let tail = 0;
  queue[tail++] = seedIdx;
  visited[seedIdx] = 1;

  while (head < tail) {
    const idx = queue[head++]!;
    component.push(idx);

    const x = idx % width;
    const y = (idx - x) / width;

    // 4-connected neighbours.
    if (y > 0) {
      const nb = idx - width;
      if (!visited[nb] && regionLabelPlane[nb] === sourceLabel) {
        visited[nb] = 1;
        queue[tail++] = nb;
      }
    }
    if (y < height - 1) {
      const nb = idx + width;
      if (!visited[nb] && regionLabelPlane[nb] === sourceLabel) {
        visited[nb] = 1;
        queue[tail++] = nb;
      }
    }
    if (x > 0) {
      const nb = idx - 1;
      if (!visited[nb] && regionLabelPlane[nb] === sourceLabel) {
        visited[nb] = 1;
        queue[tail++] = nb;
      }
    }
    if (x < width - 1) {
      const nb = idx + 1;
      if (!visited[nb] && regionLabelPlane[nb] === sourceLabel) {
        visited[nb] = 1;
        queue[tail++] = nb;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Phase 2: Count total pixels belonging to the source label.
  // -------------------------------------------------------------------------

  let totalSourcePixels = 0;
  for (let i = 0; i < n; i++) {
    if (regionLabelPlane[i] === sourceLabel) totalSourcePixels++;
  }

  // If the component covers the entire region, splitting produces no benefit.
  if (component.length === totalSourcePixels) {
    return noOp('noop');
  }

  // -------------------------------------------------------------------------
  // Phase 3: Allocate a new label (max + 1, capped at 255).
  // -------------------------------------------------------------------------

  let maxLabel = 0;
  for (const e of regionEntries) {
    if (e.label > maxLabel) maxLabel = e.label;
  }
  const newLabel = maxLabel + 1;

  if (newLabel > 255) {
    console.warn('reflood: cannot allocate new label - all 255 slots are in use.');
    return noOp('cap');
  }

  // -------------------------------------------------------------------------
  // Phase 4: Rewrite label plane and build new RegionEntry.
  // -------------------------------------------------------------------------

  const newLabelPlane = new Uint8Array(regionLabelPlane);
  for (const idx of component) {
    newLabelPlane[idx] = newLabel;
  }

  const newEntry: RegionEntry = {
    uuid: generateUuid(),
    label: newLabel,
    rgb: [...sourceEntry.rgb] as [number, number, number],
    userName: nameForLabel(newLabel),
    override: null,
    stackId: null,
  };

  const newEntries = [...regionEntries, newEntry];

  // Rebuild render-pipeline fields so the dither output stays in sync.
  const newLabelMap = rebuildLabelMap(newLabelPlane, width, height);
  const newRegions = regionConfigsFromEntries(newEntries);

  return {
    regionEntries: newEntries,
    regionLabelPlane: newLabelPlane,
    activeRegionUuids,
    labelMap: newLabelMap,
    regions: newRegions,
    status: 'ok',
  };
}
